import { ProjectSettingsPage } from "@/components/projects/settings/page";
import type {
  OverheadCategory,
  SettingsLoaderData,
  SettingsResponse,
} from "@/components/projects/settings/types";
import type { RateRow } from "@/components/rates/rates-panel";
import { ApiError, api } from "@/lib/api";
import { createFileRoute } from "@tanstack/react-router";

/**
 * `/projects/:slug/settings` — the "Edit Project" tab. The page
 * component lives in `@/components/projects/settings/page` (with its
 * cards split across sibling files) so this route file — and the
 * router's initial bundle — stays small.
 */
export const Route = createFileRoute("/_app/projects/$slug/settings")({
  loader: async ({ params }): Promise<SettingsLoaderData> => {
    const settings = await api.get<SettingsResponse>(`/api/projects/${params.slug}/settings`);
    // `/api/rates` is accountant-only. Try the call, swallow a 403
    // for non-accountant viewers (managers may still edit the
    // project, but never see the rates section).
    let billingRates: RateRow[] | null = null;
    try {
      const r = await api.get<{ rates: RateRow[] }>(
        `/api/rates?type=billing&projectId=${encodeURIComponent(settings.project.id)}&pageSize=200`,
      );
      billingRates = r.rates;
    } catch (err) {
      if (!(err instanceof ApiError && err.status === 403)) throw err;
    }
    // Overhead categories — every project carries Misc + Communication
    // out of the box; the card lets managers add more or rename /
    // delete the custom ones.
    let overheads: OverheadCategory[] = [];
    try {
      overheads = await api.get<OverheadCategory[]>(
        `/api/projects/${encodeURIComponent(settings.project.id)}/overheads`,
      );
    } catch {
      overheads = [];
    }
    return { ...settings, billingRates, overheads };
  },
  component: ProjectSettingsPage,
});
